import {Component, h} from "preact";
import {connect} from "redux-zero/preact";
import {withDeviceIndicator} from "../../hoc/withDeviceIndicator";
import actions from "../../store/actions";
import App from "../../components/app";
import DetailDesktop from "../../components/detail/desktop";
import DetailMobile from "../../components/detail/mobile";
import {productService} from "../../services/product-service";
import {Product, Variant} from "../../models/product";
import {customerService} from "../../services/customer-service";
import {Like} from "../../models/customer";
import Interstitial from "../../components/interstitial";
import {NY_Tracker} from "../../services/tracking-service";
import {i18nService} from "../../services/i18n-service";
import {imageService} from "../../services/image-service";
import {updateMetadata} from "../../hoc/metadata";
import {ProductGroup, WithContext, Product as ProductSchema} from "schema-dts";
import {config} from "../../config";
import {filterService} from "../../services/filter-service";

interface Props {
  setLikes(likes: Like[]);

  isInterstitialOpen: boolean;

  productId?: string;
  variantId?: string;
  isMobile: boolean;

  clearAllFilters();
}

const mapToProps = ({isInterstitialOpen}) => ({isInterstitialOpen});

interface State {
  product: Product;
  variant: Variant;
  notAvailable: boolean;
  title: string;
  description: string;
}

export class Detail extends Component<Props, State> {

  componentWillMount() {
    this.setState({
      title: document.title,
      description: document.querySelector("meta[name='description']")?.getAttribute("content")
    })
  }

  async componentDidMount(): Promise<void> {
    window.scrollTo(0, 0);
    this.setState({notAvailable: true});
    await this.loadProduct(this.props.productId, this.props.variantId);
    NY_Tracker.trackPage(document.location.pathname, document.title);
    customerService.subscribeForCustomer(async (data) => {
      if (!customerService.isLoggedIn) {
        this.props.setLikes([]);
      }
      if (data) {
        const {userLikes} = await customerService.loadFavouriteProducts(App.countryCode);
        this.props.setLikes(userLikes).then(() => customerService.likeCachedVariant());
      }
    });
  }

  componentWillUnmount() {
    updateMetadata({
      title: this.state.title,
      description: this.state.description
    })

    document.querySelector("script[type='application/ld+json']")?.remove();
  }

  async componentWillReceiveProps(nextProps: Props) {
    if (nextProps.productId != this.props.productId || nextProps.variantId != this.props.variantId) {
      await this.loadProduct(nextProps.productId, nextProps.variantId);
      NY_Tracker.trackPage(document.location.pathname, document.title);
    }
  }


  private async loadProduct(productId: string, variantId: string) {
    let product = await productService.loadProductById(productId, App.countryCode);
    let variant: Variant | null = null;

    if (product) {
      for (const pv of product.variants) {
        if (pv.productId === productId && pv.id === variantId) {
          variant = pv;
          break;
        }
      }
      this.setState({product: product, variant: variant});

      if (variant) {
        const metadataText = `${i18nService.translate(productService.getTranslationKey(product))} | ${variant.getColorTranslation()} | NEW YORKER`;

        updateMetadata({
          title: metadataText,
          description: metadataText,
          canonical: window.location.href,
          image: imageService.getUrl(variant?.images.find(e => e.type === 'CUTOUT'), 'low'),
          imageAlt: i18nService.translate(productService.getTranslationKey(product))
        });

        await this.loadStructuredData(product);
      }
    }
    this.setState({notAvailable: false});
  }

  async loadStructuredData(product: Product) {
    document.querySelector("script[type='application/ld+json']")?.remove();

    if (!product) return;

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");

    const filterMap = await filterService.initFilterMap();
    const filter = filterMap.categories.find(f => f.filters.find(a => a.value == product.web_category));

    const variesBy = ["https://schema.org/color"]
    if (product.variants.find(v => v.sizes.length > 0)) {
          variesBy.push("https://schema.org/size")
    }

    script.innerText = JSON.stringify({
      "@context": "https://schema.org",
      "@type":"ProductGroup",
      "name": i18nService.translate(productService.getTranslationKey(product)),
      "description": i18nService.translate(productService.getTranslationKey(product)),
      "productGroupID": product.id,
      "brand": {
          "@type": "Brand",
          "name": product.brand
      },
      "variesBy": variesBy,
      "audience": {
          "@type": "PeopleAudience",
          "suggestedGender": product.customer_group.toLowerCase(),
      },
      "hasVariant": product.variants.filter(v => !!v.currentPrice).flatMap(variant => {
        let image = variant.images.find(i => i.type === 'OUTFIT_IMAGE')
        if (!image) {
            image = variant.images.find(i => i.type === 'CUTOUT')
        }

        const colorSwatch = variant.images.find(i => i.hasThumbnail)

        const prod: WithContext<ProductSchema> = {
            "@context": "https://schema.org",
            "@type": "Product",
            "sku": variant.productId,
            "image": imageService.getUrl(image, 'high'),
            "name": i18nService.translate(productService.getTranslationKey(product)),
            "description": `${i18nService.translate(productService.getTranslationKey(product))} | ${variant.getColorTranslation()} | NEW YORKER`,
            "color": variant.getColorTranslation(),
            "material": [...new Set(variant.components.flatMap(component => component.materials.map(material => i18nService.translate(material.translationKey()))))].join(", "),
            "productID": variant.productId,
            "colorSwatch": colorSwatch ? imageService.getThumbnailUrl(colorSwatch.key) : "",
            "category": filter ? i18nService.translate(`filter_${filter.name}`) : "",
            "brand": {
                "@type": "Brand",
                "name": product.brand
            },
            "offers": {
                "@type": "Offer",
                "itemCondition": "https://schema.org/NewCondition",
                "url": `https://www.newyorker.de${config.url(`/detail/${variant.productId}/${variant.id}`)}`,
                "priceCurrency": variant.currency,
                "price": variant.currentPrice,
                "seller": {
                    "@type": "Organization",
                    "name": "NEW YORKER"
                }
            }
        }

        if (variant.sizes.length > 0) {
            return variant.sizes.map(size => ({...prod, "size": size.sizeName}))
        }

        return prod
      })
    } as WithContext<ProductGroup>);

    document.head.appendChild(script);
  }

  render() {
    if (this.state.notAvailable) {
      return null;
    }

    return (
      <div>
        {this.props.isInterstitialOpen ? <Interstitial/> : null}
        {this.props.isMobile && <DetailMobile product={this.state.product} variant={this.state.variant}/>}
        {!this.props.isMobile && <DetailDesktop product={this.state.product} variant={this.state.variant}/>}
      </div>
    )
  }
}

export default connect(mapToProps, actions)(withDeviceIndicator(Detail));
